/* eslint-disable no-restricted-properties */
import { schemas, useClient } from '@weenat/client'
import { Href } from '@weenat/client/dist/routx/runtime-core'
import { useIntl } from '@weenat/wintl'
import { Redirect, useNavigate } from 'app/routx-router'
import { clearAllStates } from 'app/src/authentication/components/Logout'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Button from 'app/src/kit/Button'
import DelimitedFlex from 'app/src/kit/DelimitedFlex'
import Icons from 'app/src/kit/Icons'
import QRCode from 'app/src/kit/QRCode'
import SubmitButton from 'app/src/kit/SubmitButton'
import SuperForm from 'app/src/kit/SuperForm'
import Text from 'app/src/kit/Text'
import EmailField from 'app/src/kit/fields/EmailField'
import { useLoginAs, useToken } from 'app/state'
import { useFormikContext } from 'formik'
import { isEmpty } from 'lodash-es'
import isNil from 'lodash-es/isNil'
import { useLocation } from 'react-router-dom'

const DEFAULT_DEBUG_REDIRECT = '/farms'
const schema = schemas.shared.mailed

function trimAndLower(str: string) {
  return str.trim().toLowerCase()
}

const FormAwareQrCodeButton = ({ onPress, isError }: { onPress: () => void; isError: boolean }) => {
  const { isValid } = useFormikContext<typeof schema.initialValues>()

  return (
    <Button
      importance='sd'
      backgroundColor='transparent'
      color='grayscale.black'
      variant='compact'
      isError={isError}
      IconLeft={Icons.QrCode}
      onPress={onPress}
      isDisabled={!isValid}
    >
      Generate QR Code
    </Button>
  )
}

const FormAwareQrCode = () => {
  const { values } = useFormikContext<typeof schema.initialValues>()

  if (isEmpty(values.email)) return null

  const debugUrl = new URL(
    `${window.location.protocol}//${window.location.hostname}${!isNil(window.location.port) ? `:${window.location.port}` : ''}${window.location.pathname}`
  )

  debugUrl.pathname = `/me/debug`
  debugUrl.searchParams.set('login_as', trimAndLower(values.email))
  debugUrl.searchParams.set('redirect', DEFAULT_DEBUG_REDIRECT)

  const debugLink = debugUrl.toString()

  return (
    <DelimitedFlex
      $isDelimitedOnTop
      $flexDirection='column'
      $alignItems='center'
      $justifyContent='center'
      $gap='lg'
      $mt={'xl'}
      $p={'xl'}
    >
      <Text $textAlign='center'>
        Use this QR code to login as <strong>{values.email}</strong> on your phone
      </Text>
      <Text $textAlign='center'>
        Please note that the Weenat app must be installed on your phone for this to work.
      </Text>
      <QRCode value={debugLink} size={128} />
    </DelimitedFlex>
  )
}

type LoginAsProps = { initialEmail?: string; unknownEmail?: string; redirect?: string }

const LoginAs = ({ initialEmail, unknownEmail, redirect }: LoginAsProps) => {
  const nav = useNavigate()
  const location = useLocation()
  const { t } = useIntl()
  const client = useClient()
  const [loginAs, setLoginAs] = useLoginAs()
  const [token] = useToken()

  const qrCodeDisclosure = useDisclosure()

  const handleSubmit = async ({ email }: { email: string }) => {
    if (!isNil(token)) {
      const formattedEmail = trimAndLower(email)

      try {
        /** we don't know if email will be valid so we are not using react-query here
         * otherwize it's too difficult to keep meRequest and debug meRequest in sync
         * here once query is valid we setLoginAs and flush all query from react-query
         * which will make userProvider to refetch data with loginAs and make debug successful
         **/
        const me = await client.me.get({ loginAsEmail: formattedEmail }).request()
        await clearAllStates(true)
        setLoginAs({ email: me.email, id: me.id })
        nav((redirect as Href) ?? DEFAULT_DEBUG_REDIRECT)
      } catch {
        nav('/me/debug', { search: { unknownEmail: formattedEmail } })
      }
    }
  }

  const isNotLoggedInsAsWantedEmail = !isNil(initialEmail) && loginAs?.email !== initialEmail
  // if debug url include another email that the current one we trigger a submit of provided email
  if (isNotLoggedInsAsWantedEmail) {
    handleSubmit({ email: initialEmail })
    //else we redirect directly because loginAs is already set to the correct email
  } else if (!isNil(redirect)) {
    return <Redirect to={redirect} />
  }

  return (
    <Flex $flexDirection='column' $mx='auto' $maxWidth={640}>
      <SuperForm
        schema={schema}
        onSubmit={handleSubmit}
        initialValues={{
          email: !isNil(location.state?.email) ? location.state?.email : initialEmail
        }}
      >
        <Flex $flexDirection='column' $flex={1}>
          <EmailField
            $displayedError={
              !isNil(unknownEmail) ? `Incorrect email address : ${unknownEmail}` : undefined
            }
          />
          <Flex $alignSelf='flex-end' $gap={'md'}>
            <FormAwareQrCodeButton onPress={qrCodeDisclosure.open} isError={!isNil(unknownEmail)} />
            <SubmitButton
              variant='compact'
              backgroundColor='grayscale.black'
              isError={!isNil(unknownEmail)}
            >
              {t('actions.confirm')}
            </SubmitButton>
          </Flex>
        </Flex>
        {qrCodeDisclosure.isOpen ? <FormAwareQrCode /> : null}
      </SuperForm>
    </Flex>
  )
}

export default LoginAs
